import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { FileText, X, AlertCircle, Loader } from "lucide-react";

const Facturacion = () => {
    const { user } = useUserAuth();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        rfc: "",
        razonSocial: "",
        regimenFiscal: "",
        usoCFDI: "",
        email: "",
        codigoPostal: "",
        folioTicket: "",
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};
        if (!formData.rfc.trim()) newErrors.rfc = "El RFC es obligatorio";
        else if (!/^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/.test(formData.rfc))
            newErrors.rfc = "RFC inválido";
        if (!formData.razonSocial.trim())
            newErrors.razonSocial = "La razón social es obligatoria";
        if (!formData.regimenFiscal) newErrors.regimenFiscal = "Selecciona un régimen fiscal";
        if (!formData.usoCFDI) newErrors.usoCFDI = "Selecciona un uso del CFDI";
        if (!formData.email.trim()) newErrors.email = "El correo es obligatorio";
        else if (!/\S+@\S+\.\S+/.test(formData.email))
            newErrors.email = "Correo inválido";
        if (!formData.codigoPostal.trim())
            newErrors.codigoPostal = "El código postal es obligatorio";
        else if (!/^\d{5}$/.test(formData.codigoPostal))
            newErrors.codigoPostal = "Código postal inválido (5 dígitos)";
        if (!formData.folioTicket.trim())
            newErrors.folioTicket = "El folio del ticket es obligatorio";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        try {
            await addDoc(collection(db, "Facturas"), {
                userId: user ? user.uid : null, // Store userId if authenticated, null otherwise
                rfc: formData.rfc,
                razonSocial: formData.razonSocial,
                regimenFiscal: formData.regimenFiscal,
                usoCFDI: formData.usoCFDI,
                email: formData.email,
                codigoPostal: formData.codigoPostal,
                folioTicket: formData.folioTicket,
                timestamp: new Date(),
                status: "pendiente",
            });

            setSuccess(true);
        } catch (error) {
            console.error("Error saving factura:", error);
            setErrorMessage("Error al enviar la solicitud de factura. Intenta de nuevo.");
        } finally {
            setLoading(false);
        }
    };

    // Reset form for new submission
    const handleNewFactura = () => {
        setFormData({
            rfc: "",
            razonSocial: "",
            regimenFiscal: "",
            usoCFDI: "",
            email: "",
            codigoPostal: "",
            folioTicket: "",
        });
        setErrors({});
        setSuccess(false);
        setErrorMessage(null);
    };

    return (
        <div className="min-h-screen bg-[#F5EBDD] pt-24 pb-8">
            {/* Fixed Header */}
            <div className="fixed top-20 left-0 right-0 z-40 bg-[#FF0000] shadow-xl">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <h1 className="text-[#F5EBDD] text-xl md:text-2xl font-bold uppercase tracking-wide">
                            Facturación
                        </h1>
                        <button
                            onClick={() => navigate("/")}
                            className="bg-[#FFBC00] text-[#FF0000] px-4 py-3 font-semibold uppercase tracking-wide hover:bg-[#000000] hover:text-[#F5EBDD] transition-all flex items-center active:scale-95 rounded-none"
                        >
                            <X className="w-5 h-5 mr-2" />
                            <span className="hidden md:inline">Volver</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="pt-20 pb-8">
                <div className="max-w-lg mx-auto px-4">
                    {/* Login Recommendation */}
                    {!user && (
                        <div className="bg-white p-6 shadow-xl mb-6">
                            <div className="flex items-center mb-4">
                                <div className="bg-[#FFBC00] p-3 mr-3">
                                    <AlertCircle className="w-6 h-6 text-[#FF0000]" />
                                </div>
                                <h3 className="text-[#FF0000] font-bold text-lg uppercase tracking-wide">
                                    Inicia sesión para guardar tus datos fiscales
                                </h3>
                            </div>
                            <p className="text-[#000000] text-sm mb-4">
                                Inicia sesión para enviar tu solicitud de factura y guardarla en tu cuenta.
                            </p>
                            <button
                                onClick={() => navigate("/sign-in")}
                                className="w-full bg-[#FF0000] text-[#F5EBDD] py-3 font-bold uppercase tracking-wide hover:bg-[#000000] transition-all active:scale-95 rounded-none"
                            >
                                Iniciar Sesión
                            </button>
                        </div>
                    )}

                    {/* Error Message */}
                    {errorMessage && (
                        <div className="bg-white p-6 shadow-xl mb-6 text-center">
                            <div className="bg-[#FF0000] p-4 inline-block mb-4">
                                <AlertCircle className="w-10 h-10 text-[#F5EBDD]" />
                            </div>
                            <h3 className="text-[#FF0000] font-bold text-lg uppercase tracking-wide">
                                Error
                            </h3>
                            <p className="text-[#000000] text-sm mt-2 mb-4">{errorMessage}</p>
                            <button
                                onClick={() => setErrorMessage(null)}
                                className="bg-[#FF0000] text-[#F5EBDD] px-6 py-3 font-bold uppercase tracking-wide hover:bg-[#000000] transition-all active:scale-95 rounded-none"
                            >
                                Intentar de nuevo
                            </button>
                        </div>
                    )}

                    {/* Success Message */}
                    {success ? (
                        <div className="bg-white shadow-xl p-6 border-2 border-[#FF0000]">
                            <div className="flex items-center mb-6">
                                <div className="bg-[#FFBC00] p-3 mr-3">
                                    <FileText className="w-8 h-8 text-[#FF0000]" />
                                </div>
                                <h2 className="text-2xl font-bold text-[#FF0000] uppercase tracking-wide">
                                    Solicitud Enviada
                                </h2>
                            </div>
                            <p className="text-[#000000] text-base font-medium mb-4">
                                ¡Gracias por llenar los datos! En un máximo de 48 horas te enviaremos la factura a tu correo: <span className="font-bold">{formData.email}</span>
                            </p>
                            <p className="text-[#000000] text-base font-medium mb-6">
                                Status de la factura: <span className="font-bold text-[#FF0000]">Pendiente</span>
                            </p>
                            <div className="space-y-3 mb-6">
                                <div>
                                    <span className="text-[#FF0000] text-xs font-bold uppercase tracking-wide">RFC:</span>
                                    <p className="text-[#000000] text-sm">{formData.rfc}</p>
                                </div>
                                <div>
                                    <span className="text-[#FF0000] text-xs font-bold uppercase tracking-wide">Razón Social:</span>
                                    <p className="text-[#000000] text-sm">{formData.razonSocial}</p>
                                </div>
                                <div>
                                    <span className="text-[#FF0000] text-xs font-bold uppercase tracking-wide">Régimen Fiscal:</span>
                                    <p className="text-[#000000] text-sm">
                                        {formData.regimenFiscal} - {
                                            {
                                                "601": "General de Ley Personas Morales",
                                                "603": "Personas Morales con Fines no Lucrativos",
                                                "605": "Sueldos y Salarios e Ingresos Asimilados a Salarios",
                                                "606": "Arrendamiento",
                                                "607": "Régimen de Enajenación o Adquisición de Bienes",
                                                "608": "Demás ingresos",
                                                "609": "Consolidación",
                                                "610": "Residentes en el Extranjero sin Establecimiento Permanente en México",
                                                "611": "Ingresos por Dividendos (socios y accionistas)",
                                                "612": "Personas Físicas con Actividades Empresariales y Profesionales",
                                                "614": "Ingresos por intereses",
                                                "615": "Régimen de los ingresos por obtención de premios",
                                                "616": "Sin obligaciones fiscales",
                                                "620": "Sociedades Cooperativas de Producción que optan por diferir sus ingresos",
                                                "621": "Incorporación Fiscal",
                                                "622": "Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras",
                                                "623": "Opcional para Grupos de Sociedades",
                                                "624": "Coordinados",
                                                "625": "Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas",
                                                "626": "Régimen Simplificado de Confianza",
                                                "628": "Hidrocarburos",
                                                "629": "De los Regímenes Fiscales Preferentes y de las Empresas Multinacionales",
                                                "630": "Enajenación de acciones en bolsa de valores",
                                            }[formData.regimenFiscal]
                                        }
                                    </p>
                                </div>
                                <div>
                                    <span className="text-[#FF0000] text-xs font-bold uppercase tracking-wide">Uso del CFDI:</span>
                                    <p className="text-[#000000] text-sm">
                                        {formData.usoCFDI} - {
                                            {
                                                "G03": "Gastos en general",
                                                "P01": "Por definir",
                                                "S01": "Sin efectos fiscales",
                                            }[formData.usoCFDI]
                                        }
                                    </p>
                                </div>
                                <div>
                                    <span className="text-[#FF0000] text-xs font-bold uppercase tracking-wide">Correo Electrónico:</span>
                                    <p className="text-[#000000] text-sm">{formData.email}</p>
                                </div>
                                <div>
                                    <span className="text-[#FF0000] text-xs font-bold uppercase tracking-wide">Código Postal:</span>
                                    <p className="text-[#000000] text-sm">{formData.codigoPostal}</p>
                                </div>
                                <div>
                                    <span className="text-[#FF0000] text-xs font-bold uppercase tracking-wide">Folio del Ticket:</span>
                                    <p className="text-[#000000] text-sm">{formData.folioTicket}</p>
                                </div>
                            </div>
                            <button
                                onClick={handleNewFactura}
                                className="w-full bg-[#FF0000] text-[#F5EBDD] py-4 font-bold uppercase tracking-wide flex items-center justify-center rounded-none hover:bg-[#000000] transition-all active:scale-95"
                            >
                                Volver a Facturar
                            </button>
                        </div>
                    ) : (
                        /* Form */
                        <div className="bg-white shadow-xl p-6">
                            <div className="flex items-center mb-6">
                                <div className="bg-[#FFBC00] p-3 mr-3">
                                    <FileText className="w-8 h-8 text-[#FF0000]" />
                                </div>
                                <h2 className="text-2xl font-bold text-[#FF0000] uppercase tracking-wide">
                                    Generar Factura
                                </h2>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* RFC */}
                                <div>
                                    <label className="text-xs text-[#FF0000] font-bold uppercase tracking-wide">
                                        RFC *
                                    </label>
                                    <input
                                        type="text"
                                        name="rfc"
                                        value={formData.rfc}
                                        onChange={handleInputChange}
                                        placeholder="XAXX010101000"
                                        className="w-full bg-[#F5EBDD] text-[#000000] p-3 rounded-none border-2 border-[#FF0000] focus:border-[#FFBC00] focus:outline-none font-medium hover:bg-[#FFF8E1] transition-all"
                                        aria-label="RFC"
                                    />
                                    {errors.rfc && (
                                        <p className="text-[#FF0000] text-xs mt-1">{errors.rfc}</p>
                                    )}
                                </div>

                                {/* Razón Social */}
                                <div>
                                    <label className="text-xs text-[#FF0000] font-bold uppercase tracking-wide">
                                        Razón Social *
                                    </label>
                                    <input
                                        type="text"
                                        name="razonSocial"
                                        value={formData.razonSocial}
                                        onChange={handleInputChange}
                                        placeholder="Nombre o empresa"
                                        className="w-full bg-[#F5EBDD] text-[#000000] p-3 rounded-none border-2 border-[#FF0000] focus:border-[#FFBC00] focus:outline-none font-medium hover:bg-[#FFF8E1] transition-all"
                                        aria-label="Razón Social"
                                    />
                                    {errors.razonSocial && (
                                        <p className="text-[#FF0000] text-xs mt-1">{errors.razonSocial}</p>
                                    )}
                                </div>

                                {/* Régimen Fiscal */}
                                <div>
                                    <label className="text-xs text-[#FF0000] font-bold uppercase tracking-wide">
                                        Régimen Fiscal *
                                    </label>
                                    <select
                                        name="regimenFiscal"
                                        value={formData.regimenFiscal}
                                        onChange={handleInputChange}
                                        className="w-full bg-[#F5EBDD] text-[#000000] p-3 rounded-none border-2 border-[#FF0000] focus:border-[#FFBC00] focus:outline-none font-medium hover:bg-[#FFF8E1] transition-all"
                                        aria-label="Régimen Fiscal"
                                    >
                                        <option value="">Selecciona un régimen</option>
                                        <option value="601">601 - General de Ley Personas Morales</option>
                                        <option value="603">603 - Personas Morales con Fines no Lucrativos</option>
                                        <option value="605">605 - Sueldos y Salarios e Ingresos Asimilados a Salarios</option>
                                        <option value="606">606 - Arrendamiento</option>
                                        <option value="607">607 - Régimen de Enajenación o Adquisición de Bienes</option>
                                        <option value="608">608 - Demás ingresos</option>
                                        <option value="609">609 - Consolidación</option>
                                        <option value="610">610 - Residentes en el Extranjero sin Establecimiento Permanente en México</option>
                                        <option value="611">611 - Ingresos por Dividendos (socios y accionistas)</option>
                                        <option value="612">612 - Personas Físicas con Actividades Empresariales y Profesionales</option>
                                        <option value="614">614 - Ingresos por intereses</option>
                                        <option value="615">615 - Régimen de los ingresos por obtención de premios</option>
                                        <option value="616">616 - Sin obligaciones fiscales</option>
                                        <option value="620">620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos</option>
                                        <option value="621">621 - Incorporación Fiscal</option>
                                        <option value="622">622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras</option>
                                        <option value="623">623 - Opcional para Grupos de Sociedades</option>
                                        <option value="624">624 - Coordinados</option>
                                        <option value="625">625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas</option>
                                        <option value="626">626 - Régimen Simplificado de Confianza</option>
                                        <option value="628">628 - Hidrocarburos</option>
                                        <option value="629">629 - De los Regímenes Fiscales Preferentes y de las Empresas Multinacionales</option>
                                        <option value="630">630 - Enajenación de acciones en bolsa de valores</option>
                                    </select>
                                    {errors.regimenFiscal && (
                                        <p className="text-[#FF0000] text-xs mt-1">{errors.regimenFiscal}</p>
                                    )}
                                </div>

                                {/* Uso del CFDI */}
                                <div>
                                    <label className="text-xs text-[#FF0000] font-bold uppercase tracking-wide">
                                        Uso del CFDI *
                                    </label>
                                    <select
                                        name="usoCFDI"
                                        value={formData.usoCFDI}
                                        onChange={handleInputChange}
                                        className="w-full bg-[#F5EBDD] text-[#000000] p-3 rounded-none border-2 border-[#FF0000] focus:border-[#FFBC00] focus:outline-none font-medium hover:bg-[#FFF8E1] transition-all"
                                        aria-label="Uso del CFDI"
                                    >
                                        <option value="">Selecciona un uso</option>
                                        <option value="G03">G03 - Gastos en general</option>
                                        <option value="P01">P01 - Por definir</option>
                                        <option value="S01">S01 - Sin efectos fiscales</option>
                                    </select>
                                    {errors.usoCFDI && (
                                        <p className="text-[#FF0000] text-xs mt-1">{errors.usoCFDI}</p>
                                    )}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="text-xs text-[#FF0000] font-bold uppercase tracking-wide">
                                        Correo Electrónico *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="tucorreo@ejemplo.com"
                                        className="w-full bg-[#F5EBDD] text-[#000000] p-3 rounded-none border-2 border-[#FF0000] focus:border-[#FFBC00] focus:outline-none font-medium hover:bg-[#FFF8E1] transition-all"
                                        aria-label="Correo Electrónico"
                                    />
                                    {errors.email && (
                                        <p className="text-[#FF0000] text-xs mt-1">{errors.email}</p>
                                    )}
                                </div>

                                {/* Código Postal */}
                                <div>
                                    <label className="text-xs text-[#FF0000] font-bold uppercase tracking-wide">
                                        Código Postal *
                                    </label>
                                    <input
                                        type="text"
                                        name="codigoPostal"
                                        value={formData.codigoPostal}
                                        onChange={handleInputChange}
                                        placeholder="12345"
                                        className="w-full bg-[#F5EBDD] text-[#000000] p-3 rounded-none border-2 border-[#FF0000] focus:border-[#FFBC00] focus:outline-none font-medium hover:bg-[#FFF8E1] transition-all"
                                        aria-label="Código Postal"
                                    />
                                    {errors.codigoPostal && (
                                        <p className="text-[#FF0000] text-xs mt-1">{errors.codigoPostal}</p>
                                    )}
                                </div>

                                {/* Folio del Ticket */}
                                <div>
                                    <label className="text-xs text-[#FF0000] font-bold uppercase tracking-wide">
                                        Folio del Ticket *
                                    </label>
                                    <input
                                        type="text"
                                        name="folioTicket"
                                        value={formData.folioTicket}
                                        onChange={handleInputChange}
                                        placeholder="Folio del ticket o número de orden"
                                        className="w-full bg-[#F5EBDD] text-[#000000] p-3 rounded-none border-2 border-[#FF0000] focus:border-[#FFBC00] focus:outline-none font-medium hover:bg-[#FFF8E1] transition-all"
                                        aria-label="Folio del Ticket"
                                    />
                                    {errors.folioTicket && (
                                        <p className="text-[#FF0000] text-xs mt-1">{errors.folioTicket}</p>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`w-full bg-[#FF0000] text-[#F5EBDD] py-4 font-bold uppercase tracking-wide flex items-center justify-center rounded-none ${loading
                                        ? "opacity-50 cursor-not-allowed"
                                        : "hover:bg-[#000000] transition-all active:scale-95"
                                        }`}
                                >
                                    {loading ? (
                                        <>
                                            <Loader className="w-5 h-5 mr-2 animate-spin" />
                                            Enviando...
                                        </>
                                    ) : (
                                        <>
                                            <FileText className="w-5 h-5 mr-2" />
                                            Generar Factura
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Facturacion;